import { DualRangeSlider } from "./DualRangeSlider";
import { Input } from "../shadcn/Input";
import { useState, useEffect } from "react";

interface PriceRangeSliderProps {
  range: [number, number];
  onRangeChange: (min: number, max: number) => void;
  minValue?: number;
  maxValue?: number;
  step?: number;
  isSliderEnabled?: boolean;
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const PriceRangeSlider = ({
  range,
  onRangeChange,
  minValue = 0,
  maxValue = 100000000,
  step = 10000,
  isSliderEnabled = false,
}: PriceRangeSliderProps) => {
  // Ensure we have valid numbers for the range
  // If range values are 0 (from empty string conversion), use the default min/max values
  const [minPrice, maxPrice] = range;
  const currentMinPrice = minPrice || minValue;
  const currentMaxPrice = maxPrice || maxValue;
  const currentRange = [currentMinPrice, currentMaxPrice];

  // Local state for input field values (for immediate UI updates)
  const [minInputValue, setMinInputValue] = useState(currentMinPrice.toString());
  const [maxInputValue, setMaxInputValue] = useState(currentMaxPrice.toString());
  
  // Track which input is in edit mode
  const [minEditMode, setMinEditMode] = useState(false);
  const [maxEditMode, setMaxEditMode] = useState(false);

  // Update local input values when props change
  useEffect(() => {
    setMinInputValue(currentMinPrice.toString());
    setMaxInputValue(currentMaxPrice.toString());
  }, [currentMinPrice, currentMaxPrice]);

  const handleRangeChange = (newRange: number[]) => {
    const [min, max] = newRange;
    onRangeChange(min, max);
  };


  const handleMinInputChange = (value: string) => {
    // Only accept numeric input in edit mode
    const numericValue = value.replace(/[^\d]/g, '');
    setMinInputValue(numericValue);
    const numValue = parseInt(numericValue);
    if (!isNaN(numValue) && numValue >= minValue && numValue <= maxValue && numValue <= currentMaxPrice) {
      // Valid value - update parent immediately
      onRangeChange(numValue, currentMaxPrice);
    }
  };

  const handleMaxInputChange = (value: string) => {
    // Only accept numeric input in edit mode
    const numericValue = value.replace(/[^\d]/g, '');
    setMaxInputValue(numericValue);
    const numValue = parseInt(numericValue);
    if (!isNaN(numValue) && numValue >= minValue && numValue <= maxValue && numValue >= currentMinPrice) {
      // Valid value - update parent immediately
      onRangeChange(currentMinPrice, numValue);
    }
  };

  const handleMinInputBlur = () => {
    const numValue = parseInt(minInputValue);
    if (!isNaN(numValue)) {
      const constrainedValue = Math.max(minValue, Math.min(Math.min(maxValue, currentMaxPrice), numValue));
      setMinInputValue(constrainedValue.toString());
      onRangeChange(constrainedValue, currentMaxPrice);
    } else {
      // Invalid input - revert to current value
      setMinInputValue(currentMinPrice.toString());
    }
    setMinEditMode(false);
  };

  const handleMaxInputBlur = () => {
    const numValue = parseInt(maxInputValue);
    if (!isNaN(numValue)) {
      const constrainedValue = Math.min(maxValue, Math.max(Math.max(minValue, currentMinPrice), numValue));
      setMaxInputValue(constrainedValue.toString());
      onRangeChange(currentMinPrice, constrainedValue);
    } else {
      // Invalid input - revert to current value
      setMaxInputValue(currentMaxPrice.toString());
    }
    setMaxEditMode(false);
  };

  return (
    <div className="w-full space-y-6">

      {/* DualRangeSlider */}
      {isSliderEnabled && (
        <div className="px-3">
          <DualRangeSlider
            showPlusForMax
            lableContenPos="left"
            value={currentRange}
            onValueChange={handleRangeChange}
            min={minValue}
            max={maxValue}
            step={step}
            minStepsBetweenThumbs={0}
          />
        </div>
      )}

      {/* Input Fields */}
      <div className={`flex gap-4 ${isSliderEnabled ? 'mt-6' : ''}`}>
        <div className="flex-1">
          <Input
            type="text"
            value={minEditMode ? minInputValue : formatCurrency(parseInt(minInputValue) || 0)}
            onChange={(e) => handleMinInputChange(e.target.value)}
            onFocus={() => setMinEditMode(true)}
            onBlur={handleMinInputBlur}
            className="text-gray-primary text-sm font-medium h-10 text-center border border-gray-300 rounded-lg"
            placeholder="Min Price"
          />
        </div>
        <div className="flex-1">
          <Input
            type="text"
            value={maxEditMode ? maxInputValue : formatCurrency(parseInt(maxInputValue) || 0)}
            onChange={(e) => handleMaxInputChange(e.target.value)}
            onFocus={() => setMaxEditMode(true)}
            onBlur={handleMaxInputBlur}
            className="text-gray-primary text-sm font-medium h-10 text-center border border-gray-300 rounded-lg"
            placeholder="Max Price"
          />
        </div>
      </div>
    </div>
  );
};

export default PriceRangeSlider;
